import AddIcon from "@mui/icons-material/Add";
import AddCommentIcon from "@mui/icons-material/AddComment";
import CommentIcon from "@mui/icons-material/Comment";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import UndoIcon from "@mui/icons-material/Undo";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import DeleteIconButton from "_shared/_views/buttons/delete-icon-button";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { useGetIndividualsByIdsQuery } from "marketplace/individuals/_api/individual-api";
import {
  getIndividualsByIds,
} from "marketplace/individuals/_stores/individuals-redux-slice";
import Individual from "marketplace/individuals/individual";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import TeamInvitation from "marketplace/teams/invitations/team-invitation";
import Team from "marketplace/teams/team";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { NavigateFunction, SetURLSearchParams } from "react-router";
import InvitationMessagePopover from "marketplace/team-profile/_views/invitation-message-popover";
import TeamMemberSelection from "marketplace/team-profile/_views/team-member-selection";

const MainContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
}));
const TeamContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-around",
  width: "100%",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));
const TeamList = styled(List)(({ theme }) => ({
  margin: 0,
  marginTop: 0,
  minWidth: theme.spacing(40),
  paddingBottom: theme.spacing(3),
}));
const TeamListSubheader = styled(ListSubheader)(({ theme }) => ({
  alignItems: "center",
  backgroundColor: theme.palette.common.white,
  color: theme.palette.common.black,
  display: "flex",
  fontSize: "1.3em",
  justifyContent: "space-between",
  padding: 0,
  "& > button": {
    height: "fit-content",
    width: "fit-content",
  },
}));
const NoRowsPlaceholder = styled(Typography)(({ theme }) => ({
  fontSize: "1.3em",
  paddingTop: theme.spacing(1),
}));
const TeamMemberLink = styled(Link)(() => ({
  cursor: "pointer",
}));
const RestoreIcon = styled(UndoIcon)(({ theme }) => ({
  color: theme.palette.secondary.main,
}));

type EditTeamProps = {
  className?: string;
  team: Team;
  invitations?: TeamInvitation[];
  invitedMembers?: Individual[];
  pendingInvitations?: TeamInvitation[];
  removedIndividuals?: Individual[];
  navigate: NavigateFunction;
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
  onLeaderReplaced?: (leader: Individual, promoted: boolean) => void;
  onMemberAdded: (member: Individual) => void;
  onInvitationUpdated: (member: TeamInvitation) => void;
  onMemberRemoved: (individual: Individual) => void;
  onPendingInvitationCanceled?: (individual: Individual) => void;
  onMemberRestored?: (individual: Individual) => void;
};

export default function EditTeam(props: Readonly<EditTeamProps>) {
  const {
    className,
    team,
    invitations,
    invitedMembers,
    pendingInvitations,
    removedIndividuals,
    navigate,
    searchParams,
    setSearchParams,
    onLeaderReplaced,
    onMemberAdded,
    onInvitationUpdated,
    onMemberRemoved,
    onPendingInvitationCanceled,
    onMemberRestored,
  } = props;

  const [currentMemberEditing, setCurrentMemberEditing] = useState<TeamInvitation>();
  const [pendingLeader, setPendingLeader] = useState<Individual>();
  const [popoverAnchorElement, setPopoverAnchorElement] = useState<HTMLElement | null>(null);
  const pendingLeaderIsApplied = pendingLeader?.isEqualTo(team.leader) ?? false;
  const selectedLeader = pendingLeader && !pendingLeaderIsApplied ? pendingLeader : team.leader;
  const selectedMembers =
    pendingLeader && !pendingLeaderIsApplied
      ? team.members.filter((member) => !member.isEqualTo(pendingLeader))
      : team.members;

  const confirm = useConfirmDialog();
  const { openDialog, popDialog } = useDialog();

  const invitedIndividualIds: Guid[] = useMemo(() => {
    return invitations?.map((invite) => invite.inviteeId) ?? [];
  }, [invitations]);

  const invitedIndividuals: Individual[] = useSelector(getIndividualsByIds(invitedIndividualIds));

  /**
   * The invitees the store cannot answer for yet.
   *
   * The selector drops the ids it has no entry for, so its length says how many of the invitees are
   * already known, never whether all of them are. Skipping on `invitedIndividuals.length` therefore
   * skipped the whole fetch as soon as any one invitee was cached - by a marketplace search, a
   * bookmark list, or another team on the same page - and the rest were never asked for. Those rows
   * rendered "Error: Individual not found" and stayed that way, which is why it read as a fault of
   * particular people rather than of the invitations that happened to be listed beside them.
   *
   * Asking only for the missing ones also keeps the request stable: ids that come back unresolved
   * stay out of the store, but the argument does not change, so RTK Query serves the cached result
   * instead of refetching on every render.
   */
  const missingInvitedIndividualIds: string[] = useMemo(() => {
    return invitedIndividualIds
      .filter((id) => !invitedIndividuals.some((individual) => individual.id.isEqualTo(id)))
      .map((id) => id.value);
  }, [invitedIndividualIds, invitedIndividuals]);

  const { isLoading: isLoadingInvitedIndividuals } = useGetIndividualsByIdsQuery(
    { ids: missingInvitedIndividualIds },
    { skip: !missingInvitedIndividualIds.length },
  );

  function getSelectedTeamMemberIds(): Guid[] {
    return [
      ...(selectedMembers?.map((member) => member.userId) ?? []),
      ...(selectedLeader?.userId ? [selectedLeader.userId] : []),
      ...invitedIndividuals.map((individual) => individual.userId),
      ...(invitedMembers?.map((individual) => individual.userId) ?? []),
    ];
  }

  /**
   * Handles what happens when add leader is clicked
   */
  function beginLeaderSelection() {
    openDialog({
      title: "Select Leader",
      component: (
        <TeamMemberSelection
          navigate={navigate}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          onNewSelection={handleNewLeaderSelected}
          selectLeader={true}
          selectMember={false}
          selectedTeamMemberUserIds={getSelectedTeamMemberIds()}
          popDialog={popDialog}
        />
      ),
      contentSxProps: {
        display: "flex",
        minHeight: "60vh",
      },
      MuiProps: {
        fullWidth: true,
        maxWidth: "lg",
      },
    });
  }

  /**
   * Handles when a new leader has been selected
   *
   * @param leader The new leader to replace the current leader with
   */
  async function handleNewLeaderSelected(leader: Individual) {
    if (selectedLeader) {
      const response = await confirm({
        title: "Replace Leader?",
        message: `Do you want to replace the current team leader with ${leader.profile?.firstName} ${leader.profile?.lastName}?`,
        okButtonText: "Replace",
      });

      if (response === ConfirmResponse.Cancel) return;
    }

    setPendingLeader(leader);

    if (selectedMembers?.find((member) => member.isEqualTo(leader))) {
      if (onLeaderReplaced) onLeaderReplaced(leader, false);
      return;
    }
    if (onLeaderReplaced) {
      onLeaderReplaced(leader, false);
    }
  }

  /**
   * Handles what happens when add member is clicked
   */
  function beginMemberSelection() {
    openDialog({
      title: "Select Member",
      component: (
        <TeamMemberSelection
          navigate={navigate}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          onNewSelection={handleNewMemberSelected}
          selectLeader={false}
          selectMember={true}
          selectedTeamMemberUserIds={getSelectedTeamMemberIds()}
          popDialog={popDialog}
        />
      ),
      contentSxProps: {
        display: "flex",
        minHeight: "60vh",
      },
      MuiProps: {
        fullWidth: true,
        maxWidth: "lg",
      },
    });
  }

  /**
   * Handles when a new member has been selected
   *
   * @param newMember The new member to add to the vendor-team
   */
  function handleNewMemberSelected(newMember: Individual) {
    if (getSelectedTeamMemberIds().some((id) => id.isEqualTo(newMember.userId))) {
      enqueueSnackbar("Selected user is already on vendor team", {
        variant: "info",
        preventDuplicate: true,
        autoHideDuration: 7500,
      });
      return;
    }

    onMemberAdded(newMember);
  }

  /**
   * Handles what happens when a member is removed from the vendor-team
   *
   * @param individual The individual to remove
   */
  function handleMemberRemoved(individual?: Individual) {
    if (!individual) return;
    onMemberRemoved(individual);
  }

  /**
   * Handles undoing the removal of a member from the vendor-team
   *
   * @param individual The individual to restore
   */
  function handleMemberRestored(individual: Individual) {
    onMemberRestored?.(individual);
  }

  function handlePendingInvitationCanceled(individual: Individual) {
    onPendingInvitationCanceled?.(individual);
  }

  /**
   * Handles what happens when the add/edit message popover is opened
   *
   * @param event The event that triggered the popover to open
   * @param member The member to edit the message for
   */
  function handleMessagePopoverOpened(event: React.MouseEvent<HTMLButtonElement, MouseEvent>, member?: TeamInvitation) {
    setCurrentMemberEditing(member);
    setPopoverAnchorElement(event?.currentTarget);
  }

  /**
   * Handles what happens when a vendor-team member's invitation message is changed
   *
   * @param userId The userId of the member whose message was changed
   * @param message The new message
   */
  function handleInvitationMessageChanged(userId?: Guid, message?: string) {
    setPopoverAnchorElement(null);

    if (!userId || !invitations) return;

    const invitation = invitations.find((pendingInvitation) => pendingInvitation.inviteeId?.isEqualTo(userId));
    if (!invitation) return;

    const targetInvitation = {
      ...invitation,
      message,
    } as TeamInvitation;

    onInvitationUpdated(targetInvitation);
  }

  /**
   * Opens the user profile dialog for the given individual
   *
   * @param individualId The id of the individual to open the dialog for
   */
  function openUserProfileDialog(individualId: Guid) {
    openDialog({
      component: <ViewIndividualProfile individualId={individualId} />,
      titleStyle: {
        paddingBottom: 0,
      },
      contentSxProps: {
        display: "flex",
        overflowX: "hidden",
      },
      MuiProps: {
        maxWidth: "lg",
        fullWidth: true,
      },
    });
  }

  /**
   * Renders a single individual's info as a list item
   *
   * @param userId The individual's info to render
   * @param allowDelete Whether or not to allow the user to be removed from the vendor-team
   */
  function renderIndividual(individual: Individual, allowDelete?: boolean) {
    const isRemoved: boolean = removedIndividuals?.some((ind) => ind.userId?.isEqualTo(individual.userId)) ?? false;

    return (
      <ListItem
        key={individual.id?.value}
        disableGutters
        secondaryAction={
          <>
            {allowDelete && (
              isRemoved ? (
                <Tooltip title="Undo Remove">
                  <span>
                    <IconButton edge="end" onClick={() => handleMemberRestored(individual)} size="medium">
                      <RestoreIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title="Remove Member">
                  <span>
                    <DeleteIconButton edge="end" onClick={() => handleMemberRemoved(individual)} size="medium">
                      <DeleteIcon />
                    </DeleteIconButton>
                  </span>
                </Tooltip>
              )
            )}
          </>
        }
      >
        <ListItemAvatar sx={isRemoved ? { opacity: 0.5 } : undefined}>
          <IndividualAvatar individual={individual} />
        </ListItemAvatar>
        <ListItemText
          sx={isRemoved ? { opacity: 0.5 } : undefined}
          primary={
            <Typography>
              <TeamMemberLink onClick={() => openUserProfileDialog(individual.id)}>
                {individual.profile?.fullName}
              </TeamMemberLink>
            </Typography>
          }
          secondary={isRemoved ? "Removing From Team" : "Active Member"}
        />
      </ListItem>
    );
  }

  /**
   * The row for an invitation whose invitee the store has not answered for.
   *
   * Two different states used to read the same way. An invitee still being fetched is an ordinary
   * moment in the load and says so; one the API did not return is a real gap, and only that one is
   * worth the word "error". Either way the row is still a row, so an open invitation is never
   * silently missing from the count of who has been asked to join.
   */
  function renderUnresolvedInvitee(invitation: TeamInvitation) {
    return (
      <ListItem key={invitation.inviteeId.value} disableGutters>
        <ListItemAvatar>
          <Avatar />
        </ListItemAvatar>
        <ListItemText
          primary={isLoadingInvitedIndividuals ? "Loading invitee…" : "Invitee unavailable"}
          secondary={
            isLoadingInvitedIndividuals
              ? "Pending Invitation"
              : "This invitation is still open, but we couldn't load who it was sent to."
          }
        />
      </ListItem>
    );
  }

  /**
   * Renders a single individual's invitation info as a list item
   */
  function renderIndividualInvitation(invitation: TeamInvitation) {
    const individual: Individual | undefined =
      invitedMembers?.find((member) => member.id.isEqualTo(invitation.inviteeId)) ??
      invitedIndividuals.find((individual) =>
        individual.id.isEqualTo(invitation.inviteeId),
      );

    if (!individual) {
      return renderUnresolvedInvitee(invitation);
    }

    const isRemoved: boolean = removedIndividuals?.some((ind) => ind.userId?.isEqualTo(individual.userId)) ?? false;

    return (
      <ListItem
        key={invitation.inviteeId.value}
        disableGutters
        secondaryAction={
          <>
            <Tooltip title="Cancel Invitation">
              <span>
                <DeleteIconButton edge="end" onClick={() => handlePendingInvitationCanceled(individual)} size="medium">
                  <DeleteIcon />
                </DeleteIconButton>
              </span>
            </Tooltip>
            {isRemoved && (
              <Tooltip title="Undo Remove">
                <span>
                  <IconButton edge="end" onClick={() => handleMemberRestored(individual)} size="medium">
                    <RestoreIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </>
        }
      >
        <ListItemAvatar sx={isRemoved ? { opacity: 0.5 } : undefined}>
          <IndividualAvatar individual={individual} />
        </ListItemAvatar>
        <ListItemText
          sx={isRemoved ? { opacity: 0.5 } : undefined}
          primary={
            <Typography>
              <TeamMemberLink onClick={() => openUserProfileDialog(individual.id)}>
                {individual?.profile?.fullName}
              </TeamMemberLink>
            </Typography>
          }
          secondary={isRemoved ? "Canceling Invitation" : "Pending Invitation"}
        />
      </ListItem>
    );
  }

  /**
   * Renders a single vendor-team member invite as a list item
   *
   * @param invitation The invitation to render
   * @returns
   */
  function renderTeamInvitation(invitation: TeamInvitation) {
    const individual: Individual | undefined = invitedIndividuals.find((individual) =>
      individual.id.isEqualTo(invitation.inviteeId),
    );

    if (!individual) {
      return renderUnresolvedInvitee(invitation);
    }

    const isRemoved: boolean = removedIndividuals?.some(
      (ind) => ind.userId?.isEqualTo(individual.userId)) ?? false;

    return (
      <ListItem
        key={invitation.inviteeId?.value}
        disableGutters
        secondaryAction={
          <>
            {isRemoved ? (
              <Tooltip title="Undo Remove">
                <span>
                  <IconButton edge="end" onClick={() => handleMemberRestored(individual)} size="medium">
                    <RestoreIcon />
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <>
                <Tooltip title={`${invitation.message ? "Edit" : "Add"} Custom Invitation Message`}>
                  <IconButton size="medium" onClick={(event) => handleMessagePopoverOpened(event, invitation)}>
                    {invitation.message ? <CommentIcon color="secondary" /> : <AddCommentIcon color="secondary" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove Invitation">
                  <span>
                    <DeleteIconButton edge="end" onClick={() => handleMemberRemoved(individual)} size="medium">
                      <DeleteIcon />
                    </DeleteIconButton>
                  </span>
                </Tooltip>
              </>
            )}
          </>
        }
      >
        <ListItemAvatar sx={isRemoved ? { opacity: 0.5 } : undefined}>
          {individual?.id ? (
            <IndividualAvatar individual={individual} />
          ) : (
            <Avatar />
          )}
        </ListItemAvatar>
        {(() => {
          let secondaryText: string;
          if (isRemoved) {
            secondaryText = "Revoking Invitation";
          } else {
            secondaryText = "Invited";
          }
          return (
            <ListItemText
              sx={isRemoved ? { opacity: 0.5 } : undefined}
              primary={
                <Typography>
                  <TeamMemberLink onClick={() => openUserProfileDialog(individual.id)}>
                    {individual?.profile?.fullName}
                  </TeamMemberLink>
                </Typography>
              }
              secondary={secondaryText}
            />
          );
        })()}
      </ListItem>
    );
  }

  return (
    <MainContainer className={className}>
      <TeamContainer>
        <TeamList>
          <TeamListSubheader>
            <span>Team Leader</span>
            <Button
              color="primary"
              startIcon={<AddIcon />}
              size="small"
              sx={{ paddingRight: 0 }}
              disabled={!onLeaderReplaced}
              onClick={beginLeaderSelection}
            >
              Select Leader
            </Button>
          </TeamListSubheader>
          {!selectedLeader && (
            <ListItem disableGutters>
              <NoRowsPlaceholder>None</NoRowsPlaceholder>
            </ListItem>
          )}
          {selectedLeader && renderIndividual(selectedLeader)}
        </TeamList>
        <TeamList>
          <TeamListSubheader>
            <span>Team Members</span>
            <Button
              color="primary"
              startIcon={<AddIcon />}
              size="small"
              sx={{ paddingRight: 0 }}
              disabled={!selectedLeader}
              onClick={beginMemberSelection}
            >
              Add Member
            </Button>
          </TeamListSubheader>
          {(!selectedMembers || selectedMembers.length < 1) &&
            (!invitations || invitations.length < 1) &&
            (!pendingInvitations || pendingInvitations.length < 1) && (
              <ListItem disableGutters>
                <NoRowsPlaceholder>None</NoRowsPlaceholder>
              </ListItem>
            )}
          {selectedMembers?.map((member) => renderIndividual(member, true))}
          {invitations?.map((member: TeamInvitation) => renderTeamInvitation(member))}
          {pendingInvitations?.map((invitation: TeamInvitation) => renderIndividualInvitation(invitation))}
        </TeamList>
      </TeamContainer>
      <InvitationMessagePopover
        userId={currentMemberEditing?.inviteeId ?? undefined}
        anchorEl={popoverAnchorElement}
        message={currentMemberEditing?.message}
        onCanceled={() => setPopoverAnchorElement(null)}
        onMessageChanged={(userId?: Guid, message?: string) => {
          handleInvitationMessageChanged(userId, message);
        }}
      />
    </MainContainer>
  );
}
